<template>
  <!--
    A MELHORIA QUE DEU CERTO, em papel (D0, D15). A versão antiga era o último modal azul do jogo:
    gradiente #1a3a5f, checkmark de CSS girado, botões-pílula verdes — e um "common.continue" cru
    no botão, porque a chave nunca existiu. Aqui é o FishModal de sempre (pergaminho no desktop),
    o ícone do PRÓPRIO atributo no lugar do checkmark genérico, e o "melhorar de novo" com o custo
    no botão — bloqueado com motivo quando o ouro não cobre, em vez de sumir (D11).
  -->
  <FishModal :aberto="true" :titulo="$t('upgrade.success.title')" @fechar="$emit('close')">
    <div class="melhoria-feita">
      <span class="melhoria-feita__selo">
        <FishIcon :nome="iconeDoAtributo" tamanho="grande" />
      </span>

      <p class="melhoria-feita__texto">
        {{ $t('upgrade.success.message', { stat: $t(`upgrade.stats.${stat}.title`) }) }}
      </p>

      <p class="melhoria-feita__efeito">
        <span>{{ $t('upgrade.success.newEffect') }}</span>
        <strong class="t-numero">{{ efeitoDoAtributo }}</strong>
      </p>
    </div>

    <template #acoes>
      <FishButton
        variante="primario"
        bloco
        som="compra"
        :desabilitado="!cobreProximaMelhoria"
        :motivo="cobreProximaMelhoria ? '' : $t('maps.buttons.insufficientGold')"
        @click="melhorarDeNovo"
      >
        {{ $t('upgrade.upgradeAgain') }}
        · <FishIcon nome="coins" tamanho="miudo" /> <span class="t-numero">{{ custoFormatado }}</span>
      </FishButton>
      <FishButton variante="neutro" bloco @click="$emit('close')">
        {{ $t('fishing.continue') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { FishModal, FishButton, FishIcon } from '@/shared/ui';

/** O mesmo mapa atributo→ícone da roda (Upgrade.vue) — o selo do modal é o atributo melhorado. */
const ICONE_DO_ATRIBUTO = {
  fishingPower: 'atributo-poder',
  catchRate: 'atributo-captura',
  criticalRate: 'atributo-critico',
  reelSpeed: 'atributo-recolhimento',
  currentStamina: 'atributo-energia',
  intelligence: 'atributo-inteligencia',
  charisma: 'atributo-carisma',
  luck: 'atributo-sorte',
};

export default {
  name: 'UpgradeSuccessModal',
  components: { FishModal, FishButton, FishIcon },
  props: {
    stat: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  computed: {
    iconeDoAtributo() {
      return ICONE_DO_ATRIBUTO[this.stat] || 'atributo-poder';
    },
    custoDaProximaMelhoria() {
      return this.$store.getters['upgrade/getStatCost'](this.stat);
    },
    cobreProximaMelhoria() {
      return this.$store.state.player.gold >= this.custoDaProximaMelhoria;
    },
    custoFormatado() {
      return new Intl.NumberFormat(this.$i18n.locale).format(this.custoDaProximaMelhoria);
    },
    efeitoDoAtributo() {
      const level = this.$store.getters['upgrade/getStatLevel'](this.stat);
      switch (this.stat) {
        case 'fishingPower':
          return `+${(level * 0.5).toFixed(1)} ${this.$t('upgrade.fishingPower')}`;
        case 'catchRate':
          // 0,5 ponto percentual por nível — EFEITO_POR_PONTO.catchRate no domínio.
          return `+${(level * 0.5).toFixed(1)}% ${this.$t('upgrade.catchRate')}`;
        case 'criticalRate':
          return `+${(level * 0.5).toFixed(1)}% ${this.$t('upgrade.criticalRate')}`;
        case 'reelSpeed':
          return `+${(level * 0.1).toFixed(1)} ${this.$t('upgrade.reelSpeed')}`;
        case 'currentStamina':
          return `+${level * 2} ${this.$t('upgrade.stamina')}`;
        case 'intelligence':
          return `+${level}% ${this.$t('upgrade.xpGain')}`;
        case 'charisma':
          return `+${level}% ${this.$t('upgrade.salesBonus')}`;
        case 'luck':
          return `+${level * 0.5}% ${this.$t('upgrade.luckBonus')}`;
        default:
          return `+${level}`;
      }
    },
  },
  methods: {
    async melhorarDeNovo() {
      try {
        // O modal fica aberto e os computed re-leem o store: efeito e custo viram os novos.
        await this.$store.dispatch('upgrade/upgradeStat', this.stat);
      } catch (error) {
        console.error('Failed to upgrade again:', error);
      }
    },
  }
}
</script>

<style scoped>
.melhoria-feita {
  text-align: center;
  color: var(--tinta);
}

/* O selo: o ícone do atributo num círculo de papel-sombra — o carimbo da conquista, sem verde. */
.melhoria-feita__selo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--papel-sombra);
  margin-bottom: var(--e-2);
}

.melhoria-feita__texto {
  margin: 0 0 var(--e-3);
  font-size: var(--f-2);
}

.melhoria-feita__efeito {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e-3);
  margin: 0;
  padding: var(--e-2) 0;
  border-top: 2px dashed var(--papel-sombra);
  border-bottom: 2px dashed var(--papel-sombra);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.melhoria-feita__efeito strong {
  color: var(--tinta);
  font-family: var(--fonte-display);
  font-size: var(--f-3);
}
</style>
